import { createFileRoute, Link } from "@tanstack/react-router";
import { Calendar, Clock } from "lucide-react";
import { BLOG_POSTS } from "@/lib/blog-posts";
import { HiperpersonalizacaoBanner } from "@/components/blog/HiperpersonalizacaoBanner";

const SITE = "https://kentro.com.br";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Kentro News — Blog da Kentro Sistemas" },
      {
        name: "description",
        content:
          "Conteúdos sobre IA, atendimento omnichannel, vendas e marketing para empresas brasileiras.",
      },
      { property: "og:title", content: "Kentro News — Blog" },
      {
        property: "og:description",
        content: "Insights sobre IA, atendimento omnichannel e crescimento de receita.",
      },
      { property: "og:url", content: `${SITE}/blog` },
    ],
    links: [{ rel: "canonical", href: `${SITE}/blog` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Kentro News",
          url: `${SITE}/blog`,
          inLanguage: "pt-BR",
          blogPost: BLOG_POSTS.map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            url: `${SITE}/blog/${p.slug}`,
            datePublished: p.date,
            author: { "@type": "Organization", name: p.author },
          })),
        }),
      },
    ],
  }),
  component: BlogPage,
});

function BlogPage() {
  const posts = [...BLOG_POSTS].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  return (
    <>
      <section className="gradient-hero relative overflow-hidden pt-24 pb-12">
        <div className="circuit-pattern absolute inset-0 opacity-30" />
        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold tracking-tight md:text-5xl">
            Kentro <span className="gradient-text">News</span>
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-lg text-muted-foreground">
            Conteúdos sobre IA, atendimento omnichannel, vendas e crescimento.
          </p>
        </div>
      </section>

      <HiperpersonalizacaoBanner />

      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold tracking-tight md:text-3xl">Artigos em destaque</h2>

        <Link
          to="/blog/$slug"
          params={{ slug: "whatsapp-cobranca-respostas-api-oficial-outubro-2026" }}
          className="group mt-6 flex flex-col gap-4 rounded-2xl border border-primary-glow/30 bg-card/60 p-6 transition hover:border-primary-glow/60 md:p-8"
        >
          <span className="inline-flex w-fit rounded-full border border-primary-glow/40 bg-primary-glow/10 px-2.5 py-1 text-xs font-medium uppercase tracking-wider text-primary-glow">
            WhatsApp Business · Destaque
          </span>
          <h3 className="text-xl font-bold leading-snug group-hover:text-primary-glow md:text-2xl">
            WhatsApp vai cobrar respostas de empresas na API Oficial: o que muda em outubro de 2026
          </h3>
          <p className="text-sm text-muted-foreground md:text-base">
            A partir de 1º de outubro de 2026, respostas não-template enviadas por empresas via WhatsApp API Oficial passam a ser cobradas por mensagem. Entenda o que muda, quem é afetado e como se preparar.
          </p>
          <span className="text-sm font-semibold text-primary-glow">Ler artigo →</span>
        </Link>

        <div className="mt-6 grid gap-6 md:grid-cols-2">
          {posts.map((p) => (
            <Link
              key={p.slug}
              to="/blog/$slug"
              params={{ slug: p.slug }}
              className="group flex flex-col gap-4 rounded-2xl border border-primary-glow/30 bg-card/60 p-6 transition hover:border-primary-glow/60 md:p-8"
            >
              <span className="inline-flex w-fit rounded-full border border-primary-glow/40 bg-primary-glow/10 px-2.5 py-1 text-xs font-medium uppercase tracking-wider text-primary-glow">
                {p.category} · Destaque
              </span>
              <h3 className="text-xl font-bold leading-snug group-hover:text-primary-glow md:text-2xl">
                {p.title}
              </h3>
              <p className="text-sm text-muted-foreground md:text-base">{p.description}</p>
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Calendar className="size-3.5" />
                  {new Date(p.date).toLocaleDateString("pt-BR", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="size-3.5" /> {p.readingMinutes} min
                </span>
              </div>
              <span className="text-sm font-semibold text-primary-glow">Ler artigo →</span>
            </Link>
          ))}
        </div>
      </section>

    </>
  );
}
